import { ParamValues } from '../components/Crud/useFilter.types';
import { Option, OptionValue } from './OptionsLib';

export type FilterConfig = {
  name: string;
  label: string;
  join?: boolean;
  options?: Option[] | readonly Option[];
  formatter?: (value: string) => string;
};

export type SelectedFilter = {
  name: string;
  label: string;
  formattedValue: string;
  value: OptionValue | null;
};

const buildSelectedFilterFromValue = (
  selectedValues: OptionValue | OptionValue[],
  filterConfig: FilterConfig,
): SelectedFilter[] => {
  if (Array.isArray(selectedValues) && filterConfig.join) {
    return [
      {
        value: null,
        label: filterConfig.label,
        formattedValue: selectedValues.join(' - '),
        name: filterConfig.name,
      },
    ];
  }
  if (Array.isArray(selectedValues)) {
    return selectedValues.map((value) => ({
      value,
      label: filterConfig.label,
      formattedValue: filterConfig.formatter
        ? filterConfig.formatter(value.toString())
        : value.toString(),
      name: filterConfig.name,
    }));
  }
  return [
    {
      value: selectedValues,
      label: filterConfig.label,
      formattedValue: filterConfig.formatter
        ? filterConfig.formatter(selectedValues.toString())
        : selectedValues.toString(),
      name: filterConfig.name,
    },
  ];
};

const buildSelectedFilter = (
  selectedValues: OptionValue | OptionValue[],
  filterConfig: FilterConfig,
): SelectedFilter[] => {
  if (!filterConfig.options) {
    return buildSelectedFilterFromValue(selectedValues, filterConfig);
  }

  if (Array.isArray(selectedValues)) {
    return filterConfig.options
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => ({
        ...option,
        label: filterConfig.label,
        name: filterConfig.name,
        formattedValue: filterConfig.formatter
          ? filterConfig.formatter(option.label)
          : option.label,
      }));
  }
  const uniqueItem = filterConfig.options.find((item) => selectedValues === item.value);
  return uniqueItem
    ? [
        {
          ...uniqueItem,
          name: filterConfig.name,
          label: filterConfig.label,
          formattedValue: filterConfig.formatter
            ? filterConfig.formatter(uniqueItem.label)
            : uniqueItem.label,
        },
      ]
    : [];
};

const getSelectedFilter = (
  allFilterParams: ParamValues,
  filterConfig: FilterConfig,
): SelectedFilter[] => {
  const selectedValue = allFilterParams[filterConfig.name];
  if (selectedValue) {
    return buildSelectedFilter(selectedValue as OptionValue | OptionValue[], filterConfig);
  }
  return [];
};

export const getAllSelectedFilters = (
  allFilterParams: ParamValues,
  filtersConfig: FilterConfig[],
): SelectedFilter[] => {
  let allSelectedFilters: SelectedFilter[] = [];

  filtersConfig.map((filterConfig) => {
    allSelectedFilters = [
      ...allSelectedFilters,
      ...getSelectedFilter(allFilterParams, filterConfig),
    ];
  });
  return allSelectedFilters;
};

export const getFilterQuantity = (searchParams: ParamValues, filterKey: string) => {
  const value = searchParams[filterKey];
  if (Array.isArray(value)) {
    return value.length;
  }

  return value ? 1 : null;
};

export const getNumberOfSelectedFilters = (
  searchParams: ParamValues,
  filterKeys: string[],
  ignoredFilterKeys?: string[],
): number =>
  filterKeys
    .map((filterKey) => {
      if (ignoredFilterKeys?.includes(filterKey)) {
        return 0;
      }
      const value = searchParams[filterKey];
      if (!value) {
        return 0;
      }
      if (Array.isArray(value)) {
        return value.length;
      }

      return 1;
    })
    .reduce((acc, currentValue) => acc + currentValue, 0);
